import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Home from './pages/Home';
import Programme from './pages/Programme';
import Posters from './pages/Posters';
import Timetable from './pages/Timetable';
import SessionDetail from './pages/SessionDetail';
import MyAgenda from './pages/MyAgenda';
import News from './pages/News';
import QA from './pages/QA';
import Sponsors from './pages/Sponsors';
import Map from './pages/Map';
import Admin from './pages/Admin';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Account from './pages/Account';
import NotFound from './pages/NotFound';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, AuthOnlyRoute } from './components/ProtectedRoute';
import { supabase } from './lib/supabase';
import { initializeSession, trackPageView } from './utils/analytics';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { ConferenceYearProvider } from './contexts/ConferenceYearContext';
import SkipLink from './components/SkipLink';
import ErrorBoundary from './components/ErrorBoundary';
import Loading from './components/Loading';
import OfflineBanner from './components/OfflineBanner';

// Component to track page views
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageName = location.pathname.replace('/', '') || 'home';
    trackPageView(pageName);
  }, [location]);

  return null;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize analytics session
    initializeSession();

    const testConnection = async () => {
      try {
        const { error } = await supabase.from('sessions').select('count');
        if (error) {
          // Supabase connection issue
        }
      } catch (err) {
        // Supabase connection issue
      } finally {
        setIsLoading(false);
      }
    };
    testConnection();
  }, []);

  if (isLoading) {
    return (
      <Loading
        message="Loading Communication Matters Conference App..."
        size="large"
        overlay={true}
      />
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AccessibilityProvider>
          <ConferenceYearProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="App">
              <PageTracker />
              <SkipLink />
              <Header />
              <OfflineBanner />
              <Navigation />

              <main id="main-content" tabIndex="-1">

                <Routes>
                  <Route path="/login" element={<AuthOnlyRoute><Login /></AuthOnlyRoute>} />
                  <Route path="/signup" element={<AuthOnlyRoute><Signup /></AuthOnlyRoute>} />
                  <Route path="/forgot-password" element={<AuthOnlyRoute><ForgotPassword /></AuthOnlyRoute>} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                  <Route path="/programme" element={<ProtectedRoute><Programme /></ProtectedRoute>} />
                  <Route path="/posters" element={<ProtectedRoute><Posters /></ProtectedRoute>} />
                  <Route path="/timetable" element={<ProtectedRoute><Timetable /></ProtectedRoute>} />
                  <Route path="/session/:sessionId" element={<ProtectedRoute><SessionDetail /></ProtectedRoute>} />
                  <Route path="/my-agenda" element={<ProtectedRoute><MyAgenda /></ProtectedRoute>} />
                  <Route path="/map" element={<ProtectedRoute><Map /></ProtectedRoute>} />
                  <Route path="/announcements" element={<ProtectedRoute><News /></ProtectedRoute>} />
                  <Route path="/qa" element={<ProtectedRoute><QA /></ProtectedRoute>} />
                  <Route path="/qa/:sessionId" element={<ProtectedRoute><QA /></ProtectedRoute>} />
                  <Route path="/sponsors" element={<ProtectedRoute><Sponsors /></ProtectedRoute>} />
                  <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/privacy-policy" element={<ProtectedRoute><PrivacyPolicy /></ProtectedRoute>} />
                  {/* /contact isn't a real page of its own — the conference's contact
                      details (email, phone, emergency contacts) already live in the
                      About page's Contact Information section. Redirect there instead
                      of maintaining a duplicate page. */}
                  <Route path="/contact" element={<Navigate to="/about#contact" replace />} />
                  <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
                </Routes>
              </main>

              <Footer />
              <CookieConsent />

              {/* Accessibility announcements */}
              <div id="live-region" aria-live="polite" aria-atomic="true" className="sr-only" />
            </div>
          </Router>
          </ConferenceYearProvider>
        </AccessibilityProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
